import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Brain, CheckCircle, Loader2, Globe, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language configurations
const LANGUAGES = {
  en: { name: 'English', nativeName: 'English', greeting: 'Welcome!' },
  hi: { name: 'Hindi', nativeName: 'हिंदी', greeting: 'नमस्ते!' },
  gu: { name: 'Gujarati', nativeName: 'ગુજરાતી', greeting: 'નમસ્તે!' }
};

// Scale labels in different languages
const SCALE_LABELS = {
  en: { 1: 'Strongly Disagree', 2: 'Disagree', 3: 'Neutral', 4: 'Agree', 5: 'Strongly Agree' },
  hi: { 1: 'पूर्णतः असहमत', 2: 'असहमत', 3: 'तटस्थ', 4: 'सहमत', 5: 'पूर्णतः सहमत' },
  gu: { 1: 'સંપૂર્ણ અસહમત', 2: 'અસહમત', 3: 'તટસ્થ', 4: 'સહમત', 5: 'સંપૂર્ણ સહમત' }
};

// Helper function to count words
const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

// Question Bank - All 90 questions
const QUESTION_BANK = {
  // Track A Questions (Grade 10)
  trackA: {
    core_interests: [
      { id: 1, text: "Organizing data, managing spreadsheets, or keeping detailed records.", scale: "1-5" },
      { id: 2, text: "Building, fixing, or assembling physical objects and machines.", scale: "1-5" },
      { id: 3, text: "Leading a team, debating an issue, or convincing others of your ideas.", scale: "1-5" },
      { id: 4, text: "Designing a poster, writing a creative story, or composing music.", scale: "1-5" },
      { id: 5, text: "Conducting science experiments or researching complex historical facts.", scale: "1-5" },
      { id: 6, text: "Helping classmates understand a difficult subject or listening to their problems.", scale: "1-5" },
      { id: 7, text: "Working outdoors or playing sports rather than sitting at a desk all day.", scale: "1-5" },
      { id: 8, text: "Starting a small business, managing a budget, or organizing a fundraising event.", scale: "1-5" },
      { id: 9, text: "Performing on stage, acting, or speaking to a large audience.", scale: "1-5" },
      { id: 10, text: "Following clear, step-by-step instructions and maintaining a strict daily routine.", scale: "1-5" }
    ],
    personality_traits: [
      { id: 21, text: "I feel comfortable starting conversations with people I don't know well.", scale: "1-5" },
      { id: 22, text: "I prefer a highly structured routine over spontaneous, sudden changes.", scale: "1-5" },
      { id: 23, text: "I am usually the first to apologize or seek a compromise in an argument.", scale: "1-5" },
      { id: 24, text: "I get easily overwhelmed or stressed when I have multiple deadlines at once.", scale: "1-5" },
      { id: 25, text: "I enjoy exploring new, unconventional ideas, even if they seem strange to others.", scale: "1-5" },
      { id: 26, text: "I always double-check my work for small errors before submitting it.", scale: "1-5" },
      { id: 27, text: "I prefer working alone in a quiet space rather than in a noisy, energetic group.", scale: "1-5" },
      { id: 28, text: "I care deeply about how my peers are feeling and actively try to support them.", scale: "1-5" },
      { id: 29, text: "I recover quickly and bounce back after receiving a bad grade or negative feedback.", scale: "1-5" },
      { id: 30, text: "I find it easy to imagine myself living in a completely different country or culture.", scale: "1-5" }
    ],
    cognitive: [
      { id: 76, text: "Store discounts ₹1000 item by 20%. Special sale applies additional 10% to new price. Final price?", type: "text", answer: "720" },
      { id: 77, text: "Sequence: Triangle, Square, Pentagon, Hexagon... Next shape and number of sides?", type: "text", answer: "Heptagon, 7" },
      { id: 78, text: "'All squares are rectangles. Some rectangles are blue.' What must be true?", type: "mcq", options: ["All squares blue", "Some squares blue", "Impossible to tell", "No squares blue"], answer: 2 }
    ],
    reality_check: [
      { id: 85, text: "Infinite funding, team of 50. What problem in the world do you solve?", type: "essay" },
      { id: 86, text: "Describe a Tuesday 15 years from now (environment, people, tasks).", type: "essay" }
    ]
  },
  
  // Track B Questions (Grade 12)
  trackB: {
    core_interests: [
      { id: 11, text: "Managing a sports team's tournament schedule, strategy, and equipment.", scale: "1-5" },
      { id: 12, text: "Creating a digital marketing or social media campaign for a local charity or NGO.", scale: "1-5" },
      { id: 13, text: "Debugging a computer program or finding the error in a complex code.", scale: "1-5" },
      { id: 14, text: "Teaching a younger student how to read or solve a difficult math problem.", scale: "1-5" },
      { id: 15, text: "Designing the architectural blueprint or layout for a new, eco-friendly building.", scale: "1-5" },
      { id: 16, text: "Negotiating a sponsorship deal or raising funds for a major school event.", scale: "1-5" },
      { id: 17, text: "Repairing a broken appliance, vehicle, or computer using specialized tools.", scale: "1-5" },
      { id: 18, text: "Proofreading a 20-page document to find spelling, grammatical, and formatting errors.", scale: "1-5" },
      { id: 19, text: "Organizing and running a medical or educational camp in a rural or underserved area.", scale: "1-5" },
      { id: 20, text: "Analyzing a company's financial reports to predict their future profits and losses.", scale: "1-5" }
    ],
    personality_traits: [
      { id: 31, text: "It is more important to me to have a stable, secure job than a risky, high-paying one.", scale: "1-5" },
      { id: 32, text: "I would rather be recognized for my unique creativity than for my ability to follow rules perfectly.", scale: "1-5" },
      { id: 33, text: "Making a positive, direct impact on my community is my primary goal in life.", scale: "1-5" },
      { id: 34, text: "I prefer to be the person making the final decisions, even if it means taking the blame.", scale: "1-5" },
      { id: 35, text: "I work best when I am competing against others to be the absolute best.", scale: "1-5" },
      { id: 36, text: "Having a clear, predictable daily routine makes me feel comfortable and focused.", scale: "1-5" },
      { id: 37, text: "I am highly motivated by the opportunity to learn a completely new skill from scratch.", scale: "1-5" },
      { id: 38, text: "I would rather work for a non-profit organization helping people than a large corporation.", scale: "1-5" },
      { id: 39, text: "Physical movement and being physically active are essential for my daily focus.", scale: "1-5" },
      { id: 40, text: "I value having a mentor or coach who gives me constant, direct feedback.", scale: "1-5" }
    ],
    work_environment: [
      { id: 41, text: "Working in a fast-paced, unpredictable environment where emergencies happen often.", scale: "1-5" },
      { id: 42, text: "Spending at least half my day outdoors, moving around, or on my feet.", scale: "1-5" },
      { id: 43, text: "Working in a quiet, highly structured corporate office with regular 9-to-5 hours.", scale: "1-5" },
      { id: 44, text: "Having a career that heavily involves physical fitness, coaching, or sports management.", scale: "1-5" },
      { id: 45, text: "Working on long-term, complex projects that take months or years.", scale: "1-5" },
      { id: 46, text: "Working in a career where my income fluctuates based on hustle rather than a fixed salary.", scale: "1-5" },
      { id: 47, text: "Having a job that requires constant networking, socializing, and presenting.", scale: "1-5" },
      { id: 48, text: "Working 'behind the scenes' to ensure an organization runs smoothly without public credit.", scale: "1-5" },
      { id: 49, text: "Working in rural or developing areas to directly improve local education or health.", scale: "1-5" },
      { id: 50, text: "Having a career that requires frequent travel.", scale: "1-5" },
      { id: 51, text: "I learn best by physically doing a task or using my hands rather than reading a textbook.", scale: "1-5" },
      { id: 52, text: "When faced with a complex problem, my first instinct is to map it out visually.", scale: "1-5" },
      { id: 53, text: "I maintain strict daily discipline with personal habits even when no one is watching.", scale: "1-5" },
      { id: 54, text: "If equipment breaks, I want to take it apart to see how it works and fix it myself.", scale: "1-5" },
      { id: 55, text: "I naturally notice when systems are inefficient and want to reorganize them.", scale: "1-5" },
      { id: 56, text: "I am comfortable making quick decisions without 100% of the information available.", scale: "1-5" },
      { id: 57, text: "I prefer to follow a proven, traditional method rather than a brand-new, untested approach.", scale: "1-5" },
      { id: 58, text: "I am energized by coordinating large-scale events with hundreds of moving parts.", scale: "1-5" },
      { id: 59, text: "I naturally gravitate toward mentoring younger peers.", scale: "1-5" },
      { id: 60, text: "I prefer learning through group discussions and debates rather than listening to a lecture.", scale: "1-5" }
    ],
    situational: [
      { id: 66, text: "Sports team/group losing badly at halftime. Captain You:", type: "mcq", options: ["Dictate new strategy", "Give passionate hype speech", "Ask team what's wrong and plan together", "Accept loss, just have fun"] },
      { id: 67, text: "Volunteering for charity, supply truck delayed 3 hours. You:", type: "mcq", options: ["Wait patiently", "Leave", "Organize waiting volunteers to prep or play games with crowd", "Call organizers to complain"] },
      { id: 68, text: "Discover major mistake in submitted (but ungraded) project. You:", type: "mcq", options: ["Say nothing", "Email teacher, admit mistake, ask to submit correction", "Blame computer glitch", "Panic, do nothing"] },
      { id: 69, text: "Partnered with a student you have conflicts with. You:", type: "mcq", options: ["Ask to change", "Do all work yourself", "Set strict boundaries, 50/50 work, only talk project", "Clear the air first"] },
      { id: 70, text: "School festival speaker system breaks before performance. You:", type: "mcq", options: ["Use megaphone while fixing cables yourself", "Delegate tech troubleshooting, manage performers backstage", "Freeze up", "Cancel performance"] },
      { id: 71, text: "Key sponsor pulls funding last minute for your tournament. You:", type: "mcq", options: ["Cancel event", "Scale down, pool money from participants", "Pitch emergency sponsorships next day", "Complain publicly"] },
      { id: 72, text: "Charity drive: only enough supplies for half the line. You:", type: "mcq", options: ["Full portions to first half, close doors", "Triage for most vulnerable", "Halve portions so everyone gets something", "Walk away, let others decide"] },
      { id: 73, text: "Discover technical loophole guaranteeing win, but against 'spirit' of game. You:", type: "mcq", options: ["Use it", "Tell organizers to keep it fair", "Keep secret, use if losing", "Tell all teams"] },
      { id: 74, text: "Creating strategy for new local business. First step:", type: "mcq", options: ["Copy competitors", "Design logo/socials", "Survey neighborhood residents on missing services", "Buy cheap online ads"] },
      { id: 75, text: "Mentee consistently failing despite your help. You:", type: "mcq", options: ["Remove them", "Change teaching method, ask about personal issues", "Do work for them", "Lower standards"] }
    ],
    cognitive: [
      { id: 79, text: "Train travels 60 km/h for 2 hours, speeds to 90 km/h for 1 hour. Total distance and avg speed?", type: "text", answer: "210km, 70km/h" },
      { id: 80, text: "'Scalpel' is to 'Surgeon' as _____ is to _____.", type: "mcq", options: ["Blueprint:Architect", "Patient:Hospital", "Law:Police", "Team:Coach"], answer: 0 },
      { id: 81, text: "If A is taller than B, and C is shorter than B. What must be true?", type: "mcq", options: ["C tallest", "A taller than C", "B shortest", "A and B same height"], answer: 1 },
      { id: 82, text: "Machine produces 5 bats every 20 mins. Hours to produce 60 bats?", type: "text", answer: "4 hours" },
      { id: 83, text: "Flat paper shaped like a cross (6 equal squares). Folded into closed 3D object, what shape?", type: "text", answer: "Cube" },
      { id: 84, text: "'Community org relies on funding, volunteers, public trust. Without trust, funding drops. Without volunteers, funds cannot be deployed.' Foundational pillar?", type: "mcq", options: ["Funding", "Volunteers", "Trust", "Equal"], answer: 2 }
    ],
    reality_check: [
      { id: 87, text: "If appointed Director of a local business/NGO/academy tomorrow, what is your first change and why?", type: "essay" },
      { id: 88, text: "What academic subject or skill do you struggle with, and how will you overcome it for your career?", type: "essay" },
      { id: 89, text: "Dream career involves boring tasks. What boring task will you endure, and why?", type: "essay" },
      { id: 90, text: "Ten years from now, dream career fails completely. What is Plan B and the first step to pivot?", type: "essay" }
    ]
  }
};

const StructuredCounselingPage = ({ user }) => {
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [gradeLevel, setGradeLevel] = useState('');
  const [started, setStarted] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [allQuestions, setAllQuestions] = useState([]);
  const [translatedQuestions, setTranslatedQuestions] = useState({});
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [wordCounts, setWordCounts] = useState({});
  const MIN_ESSAY_WORDS = 100;

  // Translate questions dynamically using AI
  const translateQuestions = async (questions, targetLang) => {
    if (targetLang === 'en') {
      // No translation needed for English
      const translated = {};
      questions.forEach(q => {
        translated[q.id] = { text: q.text, options: q.options || null };
      });
      return translated;
    }

    setTranslating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/counseling/translate`,
        {
          questions: questions.map(q => ({
            id: q.id,
            text: q.text,
            options: q.options || null,
            type: q.type || (q.scale ? 'scale' : 'text')
          })),
          target_language: targetLang
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.translations;
    } catch (error) {
      console.error('Translation failed:', error);
      toast.error('Translation failed. Showing English questions.');
      // Fallback to English
      const fallback = {};
      questions.forEach(q => {
        fallback[q.id] = { text: q.text, options: q.options || null };
      });
      return fallback;
    } finally {
      setTranslating(false);
    }
  };

  const startAssessment = async () => {
    if (!gradeLevel) {
      toast.error('Please select your grade level');
      return;
    }
    if (!selectedLanguage) {
      toast.error('Please select your preferred language');
      return;
    }

    const track = gradeLevel === '10' ? 'trackA' : 'trackB';
    const trackQuestions = QUESTION_BANK[track];
    
    // Flatten all questions
    const questions = [];
    Object.values(trackQuestions).forEach(category => {
      questions.push(...category);
    });

    setAllQuestions(questions);
    
    // Translate questions if not English
    const translations = await translateQuestions(questions, selectedLanguage);
    setTranslatedQuestions(translations);
    
    setStarted(true);
    toast.success(selectedLanguage === 'en' ? 'Assessment started!' : 
                  selectedLanguage === 'hi' ? 'मूल्यांकन शुरू!' : 
                  'મૂલ્યાંકન શરૂ!');
  };

  const getCurrentBatchQuestions = () => {
    const startIdx = currentBatch * 5;
    return allQuestions.slice(startIdx, startIdx + 5);
  };

  const handleAnswerChange = (questionId, value, isEssay = false) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    // Track word count for essay questions
    if (isEssay) {
      setWordCounts(prev => ({
        ...prev,
        [questionId]: countWords(value)
      }));
    }
  };

  const goToNextBatch = () => {
    const batchQuestions = getCurrentBatchQuestions();
    const allAnswered = batchQuestions.every(q => responses[q.id] !== undefined && responses[q.id] !== '');
    
    if (!allAnswered) {
      toast.error(selectedLanguage === 'en' ? 'Please answer all questions in this batch' :
                  selectedLanguage === 'hi' ? 'कृपया इस बैच में सभी प्रश्नों के उत्तर दें' :
                  'કૃપા કરીને આ બેચના બધા પ્રશ્નોના જવાબ આપો');
      return;
    }

    // Validate essay word counts
    const essayQuestions = batchQuestions.filter(q => q.type === 'essay');
    for (const q of essayQuestions) {
      const wc = wordCounts[q.id] || 0;
      if (wc < MIN_ESSAY_WORDS) {
        toast.error(
          selectedLanguage === 'en' 
            ? `Question ${q.id}: Please write at least ${MIN_ESSAY_WORDS} words. Current: ${wc} words.`
            : selectedLanguage === 'hi'
            ? `प्रश्न ${q.id}: कृपया कम से कम ${MIN_ESSAY_WORDS} शब्द लिखें। वर्तमान: ${wc} शब्द।`
            : `પ્રશ્ન ${q.id}: કૃપા કરીને ઓછામાં ઓછા ${MIN_ESSAY_WORDS} શબ્દો લખો। વર્તમાન: ${wc} શબ્દો.`
        );
        return;
      }
    }

    if ((currentBatch + 1) * 5 >= allQuestions.length) {
      // All questions completed - generate report
      generateReport();
    } else {
      setCurrentBatch(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPreviousBatch = () => {
    setCurrentBatch(prev => Math.max(0, prev - 1));
    window.scrollTo(0, 0);
  };

  const generateReport = async () => {
    setGenerating(true);
    
    console.log('Starting report generation...');
    console.log('Responses:', responses);
    console.log('Selected Language:', selectedLanguage);
    
    try {
      const token = localStorage.getItem('token');
      
      // Separate reality check responses (essay questions)
      const realityCheckIds = [85, 86, 87, 88, 89, 90];
      const regularResponses = {};
      const realityCheckResponses = {};
      
      Object.entries(responses).forEach(([qId, answer]) => {
        const questionId = parseInt(qId);
        if (realityCheckIds.includes(questionId)) {
          realityCheckResponses[questionId] = answer;
        } else {
          regularResponses[questionId] = answer;
        }
      });
      
      console.log('Regular responses:', regularResponses);
      console.log('Reality check responses:', realityCheckResponses);
      
      // Create session first
      const sessionResponse = await axios.post(
        `${API}/counseling/start-session`,
        { 
          grade_level: gradeLevel,
          language: selectedLanguage 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Session created:', sessionResponse.data);
      const sessionId = sessionResponse.data.session_id;

      // Submit all responses and generate reports
      const reportResponse = await axios.post(
        `${API}/counseling/submit-responses/${sessionId}`,
        {
          responses: regularResponses,
          reality_check: realityCheckResponses,
          language: selectedLanguage
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Report response:', reportResponse.data);
      
      if (reportResponse.data.report_id) {
        toast.success(selectedLanguage === 'en' ? 'Reports generated successfully!' :
                      selectedLanguage === 'hi' ? 'रिपोर्ट सफलतापूर्वक तैयार!' :
                      'રિપોર્ટ સફળતાપૂર્વક તૈયાર!');
        // Navigate to report view
        navigate(`/counseling/report/${reportResponse.data.report_id}`);
      } else {
        throw new Error('No report ID returned');
      }
    } catch (error) {
      console.error('Report generation error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to generate reports. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const progress = (currentBatch * 5 + getCurrentBatchQuestions().filter(q => responses[q.id]).length) / allQuestions.length * 100;

  if (!started) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-teal-50">
        <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Brain className="w-8 h-8 text-indigo-500" />
              <h1 className="font-heading text-2xl font-bold text-slate-900">Career Assessment</h1>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="ghost">
              Back to Dashboard
            </Button>
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-6 py-12">
          <Card className="bg-white/80 backdrop-blur-md rounded-2xl border border-slate-200 shadow-xl">
            <CardContent className="p-8">
              {/* Trilingual Greeting */}
              <div className="text-center mb-8">
                <Brain className="w-20 h-20 text-indigo-500 mx-auto mb-4" />
                <h2 className="font-heading text-3xl font-bold text-slate-900 mb-2">
                  Welcome! / नमस्ते! / નમસ્તે!
                </h2>
                <p className="text-lg text-slate-600">
                  Please choose your preferred language for the assessment
                </p>
                <p className="text-base text-slate-500 mt-1">
                  कृपया मूल्यांकन के लिए अपनी पसंदीदा भाषा चुनें | કૃપા કરીને મૂલ્યાંકન માટે તમારી પસંદગીની ભાષા પસંદ કરો
                </p>
              </div>

              {/* Language Selection */}
              <div className="mb-8">
                <Label className="text-lg font-semibold flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5 text-indigo-500" />
                  Select Language / भाषा चुनें / ભાષા પસંદ કરો
                </Label>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(LANGUAGES).map(([code, lang]) => (
                    <button
                      key={code}
                      data-testid={`lang-${code}-btn`}
                      onClick={() => setSelectedLanguage(code)}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        selectedLanguage === code
                          ? 'border-indigo-500 bg-indigo-50 shadow-md'
                          : 'border-slate-200 hover:border-indigo-200'
                      }`}
                    >
                      <div className="font-heading text-xl font-semibold">{lang.greeting}</div>
                      <div className="font-medium text-slate-900">{lang.nativeName}</div>
                      <div className="text-sm text-slate-500">{lang.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-teal-50 rounded-xl p-6 border border-indigo-100 mb-6">
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Assessment Structure:</h3>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5" />
                    Questions presented in batches of 5
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5" />
                    Strict evaluation (no partial credit on aptitude questions)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5" />
                    TWO reports generated (Student + Admin/Counselor)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-teal-500 mt-0.5" />
                    30-day personalized action plan
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                    <span className="text-amber-700">Descriptive answers require minimum 100 words</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-4">
                <Label className="text-lg font-semibold">
                  {selectedLanguage === 'hi' ? 'अपनी कक्षा का स्तर चुनें:' :
                   selectedLanguage === 'gu' ? 'તમારું ધોરણ સ્તર પસંદ કરો:' :
                   'Select Your Grade Level:'}
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    data-testid="grade-10-btn"
                    onClick={() => setGradeLevel('10')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      gradeLevel === '10'
                        ? 'border-indigo-500 bg-indigo-50 shadow-md'
                        : 'border-slate-200 hover:border-indigo-200'
                    }`}
                  >
                    <div className="font-heading text-lg font-semibold">
                      {selectedLanguage === 'hi' ? 'कक्षा 10 उत्तीर्ण' :
                       selectedLanguage === 'gu' ? 'ધોરણ 10 પાસ' :
                       'Grade 10 Passed'}
                    </div>
                    <div className="text-sm text-slate-600 mt-1">
                      {selectedLanguage === 'hi' ? 'स्ट्रीम चयन' :
                       selectedLanguage === 'gu' ? 'સ્ટ્રીમ પસંદગી' :
                       'Stream Selection'}
                    </div>
                  </button>

                  <button
                    data-testid="grade-12-btn"
                    onClick={() => setGradeLevel('12')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      gradeLevel === '12'
                        ? 'border-indigo-500 bg-indigo-50 shadow-md'
                        : 'border-slate-200 hover:border-indigo-200'
                    }`}
                  >
                    <div className="font-heading text-lg font-semibold">
                      {selectedLanguage === 'hi' ? 'कक्षा 12 उत्तीर्ण' :
                       selectedLanguage === 'gu' ? 'ધોરણ 12 પાસ' :
                       'Grade 12 Passed'}
                    </div>
                    <div className="text-sm text-slate-600 mt-1">
                      {selectedLanguage === 'hi' ? 'करियर मैपिंग' :
                       selectedLanguage === 'gu' ? 'કારકિર્દી મેપિંગ' :
                       'Career Mapping'}
                    </div>
                  </button>
                </div>

                <Button
                  data-testid="start-assessment-btn"
                  onClick={startAssessment}
                  disabled={!gradeLevel || !selectedLanguage || translating}
                  className="w-full rounded-full px-8 py-4 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold text-lg"
                >
                  {translating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {selectedLanguage === 'hi' ? 'प्रश्न तैयार हो रहे हैं...' :
                       selectedLanguage === 'gu' ? 'પ્રશ્નો તૈયાર થઈ રહ્યા છે...' :
                       'Preparing Questions...'}
                    </>
                  ) : (
                    selectedLanguage === 'hi' ? 'मूल्यांकन शुरू करें' :
                    selectedLanguage === 'gu' ? 'મૂલ્યાંકન શરૂ કરો' :
                    'Start Assessment'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const batchQuestions = getCurrentBatchQuestions();
  const isLastBatch = (currentBatch + 1) * 5 >= allQuestions.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-teal-50">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Brain className="w-6 h-6 text-indigo-500" />
              <span className="font-heading font-semibold">Career Assessment</span>
            </div>
            <span className="text-sm text-slate-600">
              Question {currentBatch * 5 + 1}-{Math.min((currentBatch + 1) * 5, allQuestions.length)} of {allQuestions.length}
            </span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div
              className="bg-indigo-500 h-2 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Language indicator */}
        <div className="flex items-center justify-end gap-2 mb-4 text-sm text-slate-600">
          <Globe className="w-4 h-4" />
          <span>{LANGUAGES[selectedLanguage]?.nativeName || 'English'}</span>
        </div>

        <Card className="bg-white rounded-2xl shadow-lg">
          <CardContent className="p-8">
            <div className="space-y-6">
              {batchQuestions.map((question, idx) => {
                const translated = translatedQuestions[question.id] || { text: question.text, options: question.options };
                const currentWordCount = wordCounts[question.id] || 0;
                const isEssayValid = question.type === 'essay' ? currentWordCount >= MIN_ESSAY_WORDS : true;
                
                return (
                  <div key={question.id} className="pb-6 border-b border-slate-100 last:border-0">
                    <Label className="text-base font-semibold text-slate-900 mb-3 block">
                      {currentBatch * 5 + idx + 1}. {translated.text}
                    </Label>

                    {question.scale && (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map(val => (
                            <button
                              key={val}
                              data-testid={`q${question.id}-scale-${val}`}
                              onClick={() => handleAnswerChange(question.id, val)}
                              className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                                responses[question.id] === val
                                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold'
                                  : 'border-slate-200 hover:border-indigo-200'
                              }`}
                            >
                              {val}
                            </button>
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-slate-500 px-1">
                          <span>{SCALE_LABELS[selectedLanguage]?.[1] || 'Strongly Disagree'}</span>
                          <span>{SCALE_LABELS[selectedLanguage]?.[5] || 'Strongly Agree'}</span>
                        </div>
                      </div>
                    )}

                    {question.type === 'mcq' && (
                      <div className="space-y-2">
                        {(translated.options || question.options).map((option, optIdx) => (
                          <button
                            key={optIdx}
                            data-testid={`q${question.id}-opt-${optIdx}`}
                            onClick={() => handleAnswerChange(question.id, String.fromCharCode(65 + optIdx))}
                            className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                              responses[question.id] === String.fromCharCode(65 + optIdx)
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-slate-200 hover:border-indigo-200'
                            }`}
                          >
                            {String.fromCharCode(65 + optIdx)}) {option}
                          </button>
                        ))}
                      </div>
                    )}

                    {question.type === 'text' && (
                      <Input
                        data-testid={`q${question.id}-text-input`}
                        value={responses[question.id] || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder={selectedLanguage === 'hi' ? 'अपना उत्तर दर्ज करें' :
                                    selectedLanguage === 'gu' ? 'તમારો જવાબ દાખલ કરો' :
                                    'Enter your answer'}
                        className="mt-2"
                      />
                    )}

                    {question.type === 'essay' && (
                      <div>
                        <textarea
                          data-testid={`q${question.id}-essay-input`}
                          value={responses[question.id] || ''}
                          onChange={(e) => handleAnswerChange(question.id, e.target.value, true)}
                          rows={6}
                          placeholder={selectedLanguage === 'hi' ? 'अपना विस्तृत उत्तर लिखें (न्यूनतम 100 शब्द)...' :
                                      selectedLanguage === 'gu' ? 'તમારો વિગતવાર જવાબ લખો (ઓછામાં ઓછા 100 શબ્દો)...' :
                                      'Write your detailed answer (minimum 100 words)...'}
                          className={`w-full mt-2 p-3 border rounded-lg focus:ring-2 focus:ring-indigo-100 ${
                            !isEssayValid && currentWordCount > 0 ? 'border-amber-400 bg-amber-50' : 'border-slate-200'
                          }`}
                        />
                        <div className={`flex items-center justify-between mt-2 text-sm ${
                          isEssayValid ? 'text-teal-600' : 'text-amber-600'
                        }`}>
                          <div className="flex items-center gap-1">
                            {!isEssayValid && currentWordCount > 0 && (
                              <AlertCircle className="w-4 h-4" />
                            )}
                            <span>
                              {selectedLanguage === 'hi' 
                                ? `शब्द: ${currentWordCount} / ${MIN_ESSAY_WORDS} न्यूनतम`
                                : selectedLanguage === 'gu'
                                ? `શબ્દો: ${currentWordCount} / ${MIN_ESSAY_WORDS} ન્યૂનતમ`
                                : `Words: ${currentWordCount} / ${MIN_ESSAY_WORDS} minimum`}
                            </span>
                          </div>
                          {isEssayValid && currentWordCount > 0 && (
                            <CheckCircle className="w-4 h-4 text-teal-500" />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
              <Button
                data-testid="prev-batch-btn"
                onClick={goToPreviousBatch}
                disabled={currentBatch === 0}
                variant="outline"
                className="px-6"
              >
                {selectedLanguage === 'hi' ? 'पिछला बैच' :
                 selectedLanguage === 'gu' ? 'પાછલું બેચ' :
                 'Previous Batch'}
              </Button>

              <Button
                data-testid="next-batch-btn"
                onClick={goToNextBatch}
                disabled={generating}
                className="px-8 bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {selectedLanguage === 'hi' ? 'रिपोर्ट तैयार हो रही है...' :
                     selectedLanguage === 'gu' ? 'રિપોર્ટ તૈયાર થઈ રહી છે...' :
                     'Generating Reports...'}
                  </>
                ) : isLastBatch ? (
                  selectedLanguage === 'hi' ? 'पूर्ण करें और रिपोर्ट बनाएं' :
                  selectedLanguage === 'gu' ? 'પૂર્ણ કરો અને રિપોર્ટ બનાવો' :
                  'Complete & Generate Reports'
                ) : (
                  selectedLanguage === 'hi' ? 'अगला बैच' :
                  selectedLanguage === 'gu' ? 'આગલું બેચ' :
                  'Next Batch'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StructuredCounselingPage;
